// The combinational redstone solve (RED-1/2, M15.1): given the current roles in a
// network, compute every wire's power, each conductor's block-power, and the
// signals mechanisms read. PURE (ENG-1). Delayed components (repeater/comparator/
// torch) live in engine.ts and consume this field's readers.
// The rules here are the contract in docs/redstone-semantics.md.
import { RED } from '../../config';
import {
  DIR_VEC, HORIZ, OPPOSITE, sidesOf, cellKey,
  type Dir, type Facing, type RedRole, type RedstoneAccess,
} from './types';

const MAX = RED.MAX_POWER;

/** Container fullness signal (E-9): 0 when empty, else 1..15 by average fill. */
export function containerFullness(fills: readonly number[], slots: number): number {
  if (slots <= 0) return 0;
  let sum = 0;
  let any = false;
  for (const f of fills) {
    if (f > 0) any = true;
    sum += Math.max(0, Math.min(1, f));
  }
  if (!any) return 0;
  return Math.floor(1 + (sum / slots) * 14);
}

/** Comparator front output for a mode, given the rear + strongest side signal. */
export function comparatorOutput(rear: number, side: number, mode: 'compare' | 'subtract'): number {
  return mode === 'subtract' ? Math.max(0, rear - side) : rear >= side ? rear : 0;
}

type Cell = readonly [number, number, number];

/**
 * A solved snapshot of one redstone network: wire powers + block powers + the
 * emission readers delayed components query. Constructed once per solve.
 */
export class PowerField {
  private readonly power = new Map<string, number>(); // wire cell -> dust power
  private readonly strong = new Map<string, number>(); // conductor -> strong power
  private readonly weak = new Map<string, number>(); // conductor -> weak power (from sources)
  private readonly dust: Cell[] = [];

  constructor(
    private readonly a: RedstoneAccess,
    cells: readonly Cell[],
    /** Stored comparator output level (its front emission) by cell. */
    private readonly comparatorOut: (x: number, y: number, z: number) => number,
  ) {
    const dustFrom = new Map<string, number>();
    const bump = (m: Map<string, number>, k: string, v: number): void => {
      if (v > (m.get(k) ?? 0)) m.set(k, v);
    };
    // Pass 1 — classify + apply every source's direct emissions (§3).
    for (const [x, y, z] of cells) {
      const r = this.a.roleAt(x, y, z);
      if (r.kind === 'wire') this.dust.push([x, y, z]);
      this.emitSource(r, x, y, z, dustFrom, bump);
    }
    // Pass 2 — solve wire power by max-relaxation with a per-block drop (§4).
    this.solveWire(dustFrom);
  }

  private emitSource(
    r: RedRole, x: number, y: number, z: number,
    dustFrom: Map<string, number>, bump: (m: Map<string, number>, k: string, v: number) => void,
  ): void {
    const toDust = (dir: Dir, level: number): void => {
      const [dx, dy, dz] = DIR_VEC[dir];
      const nx = x + dx, ny = y + dy, nz = z + dz;
      if (this.a.roleAt(nx, ny, nz).kind === 'wire') bump(dustFrom, cellKey(nx, ny, nz), level);
    };
    const strongInto = (dir: Dir, level: number): void => {
      const [dx, dy, dz] = DIR_VEC[dir];
      bump(this.strong, cellKey(x + dx, y + dy, z + dz), level);
    };
    const weakInto = (dir: Dir, level: number): void => {
      const [dx, dy, dz] = DIR_VEC[dir];
      const nx = x + dx, ny = y + dy, nz = z + dz;
      if (this.a.isConductor(nx, ny, nz)) bump(this.weak, cellKey(nx, ny, nz), level);
    };
    switch (r.kind) {
      case 'source': // redstone block: 15 to all adjacent dust; weak to adjacent blocks.
        for (const d of ['north', 'east', 'south', 'west', 'up', 'down'] as Dir[]) { toDust(d, MAX); weakInto(d, MAX); }
        break;
      case 'lever':
      case 'button':
        if (r.on) {
          strongInto(r.attach, MAX);
          for (const d of ['north', 'east', 'south', 'west', 'up', 'down'] as Dir[]) toDust(d, MAX);
        }
        break;
      case 'plate':
        if (r.on) {
          strongInto('down', MAX);
          for (const d of ['north', 'east', 'south', 'west', 'up'] as Dir[]) toDust(d, MAX);
        }
        break;
      case 'torch':
        if (r.lit) {
          strongInto('up', MAX);
          for (const d of ['north', 'east', 'south', 'west', 'up', 'down'] as Dir[]) if (d !== r.attach) toDust(d, MAX);
        }
        break;
      case 'repeater':
        if (r.powered) { strongInto(r.facing, MAX); toDust(r.facing, MAX); }
        break;
      case 'comparator': {
        const out = this.comparatorOut(x, y, z);
        if (r.powered && out > 0) { strongInto(r.facing, out); toDust(r.facing, out); }
        break;
      }
      case 'observer': // emits out its back (opposite the watched face) while pulsing.
        if (r.powered) { const back = OPPOSITE[r.facing]; strongInto(back, MAX); toDust(back, MAX); }
        break;
      default:
        break;
    }
  }

  private solveWire(dustFrom: Map<string, number>): void {
    const queue: Cell[] = [];
    for (const [x, y, z] of this.dust) {
      const base = Math.max(dustFrom.get(cellKey(x, y, z)) ?? 0, this.adjacentStrong(x, y, z));
      if (base > 0) { this.power.set(cellKey(x, y, z), base); queue.push([x, y, z]); }
      else this.power.set(cellKey(x, y, z), 0);
    }
    // Relax: a dust cell hands (power−1) to each connected dust neighbour.
    let guard = 0;
    while (queue.length && guard++ < RED.MAX_NETWORK * MAX) {
      const [x, y, z] = queue.shift()!;
      const p = this.power.get(cellKey(x, y, z))!;
      if (p <= 1) continue;
      for (const [nx, ny, nz] of this.connectedDust(x, y, z)) {
        const nk = cellKey(nx, ny, nz);
        if (p - 1 > (this.power.get(nk) ?? 0)) { this.power.set(nk, p - 1); queue.push([nx, ny, nz]); }
      }
    }
  }

  /** Strongest strong-power among adjacent conductors dust reads from (4 horiz + below). */
  private adjacentStrong(x: number, y: number, z: number): number {
    let best = 0;
    for (const h of HORIZ) {
      const [dx, , dz] = DIR_VEC[h];
      best = Math.max(best, this.strong.get(cellKey(x + dx, y, z + dz)) ?? 0);
    }
    best = Math.max(best, this.strong.get(cellKey(x, y - 1, z)) ?? 0);
    return best;
  }

  /** Dust cells this dust connects to: 4 horizontal, plus the standard up/down step. */
  private connectedDust(x: number, y: number, z: number): Cell[] {
    const out: Cell[] = [];
    const isWire = (cx: number, cy: number, cz: number): boolean => this.a.roleAt(cx, cy, cz).kind === 'wire';
    for (const h of HORIZ) {
      const [dx, , dz] = DIR_VEC[h];
      const nx = x + dx, nz = z + dz;
      if (isWire(nx, y, nz)) { out.push([nx, y, nz]); continue; }
      // Up: climb the side of a block if the roof over the centre is open.
      if (isWire(nx, y + 1, nz) && !this.a.isConductor(x, y + 1, z)) out.push([nx, y + 1, nz]);
      // Down: drop onto lower dust if the block over that lower cell is open.
      if (isWire(nx, y - 1, nz) && !this.a.isConductor(nx, y, nz)) out.push([nx, y - 1, nz]);
    }
    return out;
  }

  // ── Readers the engine + tests query ──────────────────────────────────────
  wirePower(x: number, y: number, z: number): number {
    return this.power.get(cellKey(x, y, z)) ?? 0;
  }

  /** Power a conductor block carries: strong ∪ source-weak ∪ dust-weak. */
  blockPower(x: number, y: number, z: number): number {
    const k = cellKey(x, y, z);
    let best = Math.max(this.strong.get(k) ?? 0, this.weak.get(k) ?? 0);
    // Dust sitting on top, and dust beside, weakly powers this block.
    best = Math.max(best, this.wirePower(x, y + 1, z));
    for (const h of HORIZ) {
      const [dx, , dz] = DIR_VEC[h];
      best = Math.max(best, this.wirePower(x + dx, y, z + dz));
    }
    return best;
  }

  /** Signal the component at (x,y,z) delivers in `incomingDir` (source→reader). */
  private emitToward(x: number, y: number, z: number, incomingDir: Dir): number {
    const r = this.a.roleAt(x, y, z);
    switch (r.kind) {
      case 'wire': return this.wirePower(x, y, z);
      case 'block': return this.blockPower(x, y, z);
      case 'source': return MAX;
      case 'lever':
      case 'button': return r.on ? MAX : 0;
      case 'plate': return r.on ? MAX : 0;
      case 'torch': return r.lit && incomingDir !== r.attach ? MAX : 0;
      case 'repeater': return r.powered && incomingDir === r.facing ? MAX : 0;
      case 'comparator': return r.powered && incomingDir === r.facing ? this.comparatorOut(x, y, z) : 0;
      case 'observer': return r.powered && incomingDir === OPPOSITE[r.facing] ? MAX : 0;
      default: return 0;
    }
  }

  /** A repeater's rear input (the cell opposite its facing). */
  repeaterInput(x: number, y: number, z: number, facing: Facing): number {
    const [dx, dy, dz] = DIR_VEC[OPPOSITE[facing]];
    return this.emitToward(x + dx, y + dy, z + dz, facing);
  }

  /** True if a powered repeater/comparator on either side points into this one. */
  repeaterLocked(x: number, y: number, z: number, facing: Facing): boolean {
    for (const s of sidesOf(facing)) {
      const [dx, dy, dz] = DIR_VEC[s];
      const r = this.a.roleAt(x + dx, y + dy, z + dz);
      const into = OPPOSITE[s];
      if ((r.kind === 'repeater' || r.kind === 'comparator') && r.powered && r.facing === into) return true;
    }
    return false;
  }

  /** A comparator's rear main signal, including a container read behind it (E-9). */
  comparatorRear(x: number, y: number, z: number, facing: Facing): number {
    const [dx, dy, dz] = DIR_VEC[OPPOSITE[facing]];
    const rx = x + dx, ry = y + dy, rz = z + dz;
    const container = this.a.containerSignal(rx, ry, rz);
    return Math.max(this.emitToward(rx, ry, rz, facing), container >= 0 ? container : 0);
  }

  /** A comparator's strongest side signal. */
  comparatorSide(x: number, y: number, z: number, facing: Facing): number {
    let best = 0;
    for (const s of sidesOf(facing)) {
      const [dx, dy, dz] = DIR_VEC[s];
      best = Math.max(best, this.emitToward(x + dx, y + dy, z + dz, OPPOSITE[s]));
    }
    return best;
  }

  /** True when a torch's attachment block is powered (→ torch should be off). */
  torchAttachPowered(x: number, y: number, z: number, attach: Dir): boolean {
    const [dx, dy, dz] = DIR_VEC[attach];
    return this.blockPower(x + dx, y + dy, z + dz) > 0;
  }
}
